import { useExplorerStore } from "@/modules/explorer/store";
import { useToasterForNotification } from "@/toaster";
import { useApiClient } from "@/api/client";
import { useHierarchyStore } from "@/stores/hierarchyStore";
import DocumentDO = App.Data.Explorer.DocumentDO;
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;
import {
	buildDatetimeFilterConfiguration,
	buildPeriodSelectionForLabel,
	findFinerGranularityDimension,
} from "./datetimeFilter";

const findHierarchyNodeByLabel = (node: any, label: string): any => {
	if (node.label === label) return node;
	for (const child of node.children ?? []) {
		const result = findHierarchyNodeByLabel(child, label);
		if (result) return result;
	}
	return null;
};

const goUpHierarchyLevel = async (activeFilter: any, legendDimension: any) => {
	const currentLevel = activeFilter.values[0].level;
	const currentLabel = activeFilter.values[0].label;

	if (currentLevel === null) return;

	const store = useExplorerStore();
	const hierarchyStore = useHierarchyStore();
	const hierarchyId = legendDimension.hierarchy_definition_id;

	const root = await hierarchyStore.getOrLoadHierarchy(
		hierarchyId,
		async () => {
			const response = await useApiClient("explorer").getHierarchy(
				null,
				hierarchyId
			);
			if (!response.isOk()) return null;
			const nodes = response.value as unknown as QuickFilterNodeDTO[];
			return nodes.length > 0 ? nodes[0] : null;
		}
	);
	if (!root) return;

	const parent = hierarchyStore.findParentByLabelAndLevel(
		root,
		currentLabel,
		currentLevel
	);
	if (!parent) return; // already at root — do nothing

	store.APPLY_FILTER(
		{
			...activeFilter,
			values: [
				{
					id: null,
					level: parent.level,
					label: parent.label,
					selection_depth: 1,
					model: "App\\Models\\HierarchyNode",
					excluded_branches: activeFilter.values[0].excluded_branches ?? [],
				},
			],
		},
		"edit"
	);
};

type Axis = "horizontal_dimension" | "legend_dimension";

/**
 * Applies a filter for a single label on a dimension, used by both the legend
 * and the horizontal axis. `valueIds` maps label → DimensionValue id when the
 * backend supplied them (legend only, see ClickhouseResponseProcessing); the
 * filter otherwise carries a null id and is resolved by label.
 */
const applyFilterForLabel = async (
	dimension: DocumentDimensionDTO | null | undefined,
	label: string,
	axis: Axis,
	valueIds?: Record<string, number>
) => {
	// console.log("Hallo?", label, dimension, valueIds);
	if (!dimension) return;

	const store = useExplorerStore();
	const activeFilter = store.GET_ACTIVE_FILTER(dimension.name_clean);
	let filterConfiguration: any = null;

	switch (dimension.type) {
		case "bucketed":
		case "nominal":
			filterConfiguration = {
				dimension,
				isApplied: true,
				isQuickFilter: false,
				isDefault: false,
				valuesAreIncluded: true,
				values: [
					{
						id: valueIds?.[label.trim()] ?? null,
						level: null,
						label: label.trim(),
						selection_depth: null,
						model: "App\\Models\\DimensionValue",
					},
				],
			};
			break;
		case "datetime": {
			// Subject date is the document's period, not a filter dimension, so
			// its granules narrow period_selection instead.
			const periodSelection = buildPeriodSelectionForLabel(
				dimension,
				label,
				store.document.period_selection
			);
			if (periodSelection) {
				store.document.period_selection = periodSelection;

				// The clicked granule is now the whole period, so leaving the axis
				// on that granularity would show a single bar. Step it one level
				// finer, when the family has a finer dimension to step to.
				const finer = findFinerGranularityDimension(
					dimension,
					axis === "legend_dimension" ? "legend" : "horizontal"
				);
				if (finer) {
					store.document[axis] = finer as unknown as DocumentDimensionDTO;
				}
				return;
			}

			filterConfiguration = await buildDatetimeFilterConfiguration(
				dimension,
				label
			);
			if (!filterConfiguration) {
				useToasterForNotification(
					`Note: Filtering on ${dimension.granularity} periods (${label}) is not yet supported.`,
					3000,
					"error"
				);
			}
			break;
		}
		case "hierarchy": {
			const excluded_branches =
				activeFilter?.values?.[0]?.excluded_branches ?? [];
			let level = activeFilter ? activeFilter.values[0].level - 1 : null;
			const isQuickFilter = activeFilter?.isQuickFilter ?? false;

			if (
				activeFilter?.dimension?.hierarchy_definition_id !==
				dimension.hierarchy_definition_id
			) {
				const response = await useApiClient("explorer").getHierarchy(
					null,
					dimension.hierarchy_definition_id
				);
				if (response.isOk()) {
					const nodes = response.value as unknown as QuickFilterNodeDTO[];
					if (nodes.length > 0) {
						const node = findHierarchyNodeByLabel(nodes[0], label);
						level = node?.level ?? null;
					}
				}
			}

			if (level >= 0) {
				filterConfiguration = {
					dimension,
					isApplied: true,
					isQuickFilter,
					isDefault: false,
					valuesAreIncluded: true,
					values: [
						{
							id: null,
							level,
							label,
							selection_depth: 1,
							model: "App\\Models\\HierarchyNode",
							excluded_branches,
						},
					],
				};
			}
			break;
		}
		default:
			console.error("Unknown dimension type:", dimension.type);
	}

	if (filterConfiguration) {
		// Not `activeFilter`: a datetime filter can target the family's `day`
		// sibling instead of the dimension that was clicked.
		const existing = store.GET_ACTIVE_FILTER(
			filterConfiguration.dimension.name_clean
		);
		store.APPLY_FILTER(filterConfiguration, existing ? "edit" : "create");
	}
};

export const useLegendActions = (derivedDocument: () => DocumentDO) => {
	const handleLegendDoubleClick = async (label: string) => {
		const store = useExplorerStore();

		// Special visibility toggles — no filter needed
		if (label === "predictionReservation") {
			store.document.is_reservation_prediction_visible = false;
			return;
		}
		if (label === "predictionDriveUp") {
			store.document.is_drive_up_prediction_visible = false;
			return;
		}
		if (label === "capacity") {
			store.document.visible_capacity_types = [];
			return;
		}
		// Split capacity legends (capacity_normal / capacity_programmatic) hide only their own type.
		if (label.startsWith("capacity_")) {
			const capacityType = label.slice("capacity_".length);
			store.document.visible_capacity_types = (
				store.document.visible_capacity_types ?? []
			).filter((type) => type !== capacityType);
			return;
		}

		await applyFilterForLabel(
			derivedDocument()?.legend_dimension,
			label,
			"legend_dimension",
			store.explorerState.lastLegendDimensionValuesIds as unknown as Record<
				string,
				number
			>
		);
	};

	/**
	 * Double-click on a horizontal axis label — same filter behaviour as the
	 * legend, minus the visibility toggles (legend-only concepts) and without
	 * value ids, which the backend only emits for the legend dimension.
	 */
	const handleHorizontalDoubleClick = async (label: string) =>
		applyFilterForLabel(
			derivedDocument()?.horizontal_dimension,
			label,
			"horizontal_dimension"
		);

	const handleContextMenuAction = async () => {
		const store = useExplorerStore();
		const legendDimension = derivedDocument()?.legend_dimension;
		if (!legendDimension) return;

		const activeFilter = store.GET_ACTIVE_FILTER(legendDimension.name_clean);
		if (!activeFilter) return;

		if (legendDimension.type === "hierarchy") {
			await goUpHierarchyLevel(activeFilter, legendDimension);
		} else {
			const index = store.document.active_filters.findIndex(
				(f) => f.dimension.name_clean === legendDimension.name_clean
			);
			if (index !== -1) {
				store.REMOVE_FILTER_BY_INDEX(index);
			}
		}
	};

	return {
		handleLegendDoubleClick,
		handleHorizontalDoubleClick,
		handleContextMenuAction,
	};
};
